import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WalletProvider } from './contexts/WalletContext';
import { WalletAdapterProvider } from './contexts/WalletAdapterContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary, { TradingErrorBoundary, WalletErrorBoundary } from './components/common/ErrorBoundary';
import './index.css';
import './styles/enhanced-animations.css';
import './styles/error-handling.css';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LandingPage from './components/landing/LandingPage';
import './styles/landing.css';
import AppLayout from './components/layout/AppLayout';
import WalletManager from './components/wallet/WalletManager';
import TokenWatchlist from './components/tokens/TokenWatchlist';
import TradingHistory from './components/trading/TradingHistory';
import TradingHistorySummary from './components/trading/TradingHistorySummary';
import TokenBrowser from './components/tokens/TokenBrowser';
import PriceMonitorDashboard from './components/trading/PriceMonitorDashboard';
import TradingDashboard from './components/trading/TradingDashboard';
import CompactTradingDashboard from './components/trading/CompactTradingDashboard';
import SolanaTokensDashboard from './components/dashboard/SolanaTokensDashboard';
import AssetsPage from './components/assets/AssetsPage';
import ToastContainer from './components/common/Toast';
import ToastDemo from './components/common/ToastDemo';
import ThemeDemo from './components/common/ThemeDemo';
import WalletPersistenceDebug from './components/debug/WalletPersistenceDebug';
import SettingsPage from './components/settings/SettingsPage';
import HeaderDemo from './components/demo/HeaderDemo';
import AlertsManager from './components/alerts/AlertsManager';
import AlertsContainer from './components/alerts/AlertsContainer';
import OpportunitiesSummary from './components/opportunities/OpportunitiesSummary';
import useOpportunityNotifications from './hooks/useOpportunityNotifications';
import AdminPoolDashboard from './components/admin/AdminPoolDashboard';
import JupiterStyleTradingInterface from './components/trading/JupiterStyleTradingInterface';
import './styles/auth.css';
import './styles/wallet.css';
import './styles/tokens.css';
import './styles/trading.css';
import './styles/trading-history.css';
// import './styles/token-browser.css';  // Temporarily disabled - contains invalid CSS
// import './styles/token-watchlist.css';
import './styles/price-monitor.css';
import './styles/price-monitor-dashboard.css';
import './styles/trading-dashboard.css';
import './styles/jupiter-trading.css';
import './styles/assets.css';
import './styles/toast.css';
import './styles/compact-trading-dashboard.css';
import './styles/compact-manual-trading.css';
import './styles/compact-automated-bot.css';
import './styles/sidebar.css';
import './styles/app-layout.css';
import './styles/enhanced-dashboard.css';
import './styles/wallet-selector.css';
import './styles/mobile-menu-toggle.css';
import './styles/app-futuristic.css';
import WatchlistTrading from './components/trading/WatchlistTrading';
import './components/alerts/PriceAlertModal.css';
import './components/alerts/AlertsManager.css';
import './components/alerts/AlertNotification.css';
import './components/alerts/AlertsContainer.css';
import './components/tokens/InvestmentTab.css';
import './components/tokens/InvestmentPoolCard.css';
import './components/tokens/InvestmentModal.css';
import './components/tokens/WithdrawalModal.css';

// Enhanced Animated Background Component
const AnimatedBackground = () => {
  return (
    <div className="animated-background">
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="particle"></div>
      <div className="gradient-orb"></div>
      <div className="gradient-orb"></div>
      <div className="gradient-orb"></div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const initialView = params.view || 'solana-tokens';
  const [currentView, setCurrentView] = useState(initialView);

  // Enable opportunity notifications for authenticated users
  useOpportunityNotifications(!!user, 0.75); // 75% confidence threshold

  const renderView = () => {
    switch (currentView) {
      case 'wallets':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>💼 Wallet Management</h1>
              <p>Manage your Solana wallets and keys securely</p>
            </div> */}
            <WalletErrorBoundary>
              <WalletManager />
            </WalletErrorBoundary>
          </div>
        );
      case 'assets':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>💰 My Assets</h1>
              <p>View and manage your token portfolio</p>
            </div> */}
            <AssetsPage />
          </div>
        );
      case 'tokens':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>📊 Token Watchlist</h1>
              <p>Track your favorite tokens and market movements</p>
            </div> */}
            <TokenWatchlist />
          </div>
        );
      case 'trading':
        return (
          <TradingErrorBoundary>
            <TradingDashboard hideGlobalSidebar={true} setCurrentView={setCurrentView} />
          </TradingErrorBoundary>
        );
      case 'compact-trading':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>📱 Enhanced Trading</h1>
              <p>Compact trading interface for quick trades</p>
            </div> */}
            <TradingErrorBoundary >
              <CompactTradingDashboard setCurrentView={setCurrentView} />
            </TradingErrorBoundary>
          </div>
        );
      case 'history':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>📈 Trading History</h1>
              <p>Review your past trades and performance</p>
            </div>
            <TradingHistory />
          </div>
        );
      case 'discover':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>🔍 Token Discovery</h1>
              <p>Discover new tokens and trading opportunities</p>
            </div> */}
            <TokenBrowser />
          </div>
        );
      case 'monitor':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>📊 Price Monitor</h1>
              <p>Real-time price monitoring and alerts</p>
            </div> */}
            <PriceMonitorDashboard />
          </div>
        );
      case 'jupiter-trading':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>🚀 Jupiter-Style Trading</h1>
              <p>Fast token swaps with a sleek, compact interface</p>
            </div> */}
            <TradingErrorBoundary>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <JupiterStyleTradingInterface />
              </div>
            </TradingErrorBoundary>
          </div>
        );
      case 'toast-demo':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>🍞 Toast Demo</h1>
              <p>Test notification system</p>
            </div> */}
            <ToastDemo />
          </div>
        );
      case 'theme-demo':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>🎨 Theme Demo</h1>
              <p>Test different themes and colors</p>
            </div>
            <ThemeDemo />
          </div>
        );
      case 'settings':
        return (
          <div className="page-content">
            <SettingsPage />
          </div>
        );
      case 'header-demo':
        return (
          <div className="page-content">
            <HeaderDemo />
          </div>
        );
      case 'solana-tokens':
        return (
          <div className="page-content">
            {/* <div className="page-header">
              <h1>🪙 Popular Solana Tokens</h1>
              <p>Top 10 trending tokens on Solana blockchain</p>
            </div> */}
            <SolanaTokensDashboard />
          </div>
        );

      case 'alerts':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>🔔 Price Alerts</h1>
              <p>Manage your token price alerts and notifications</p>
            </div>
            <AlertsManager />
          </div>
        );
      case 'opportunities':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>📈 Trading Opportunities</h1>
              <p>Discover high-potential trading opportunities with AI-powered market analysis</p>
            </div>
            <TokenBrowser initialTab="opportunities" />
          </div>
        );
      case 'investment':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>🏦 Investment Pools</h1>
              <p>Invest your SOL with professional traders and earn returns</p>
            </div>
            <TokenBrowser initialTab="investment" />
          </div>
        );
      case 'admin':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>⚙️ Pool Management</h1>
              <p>Manage investment pools, execute trades, and monitor performance</p>
            </div>
            <AdminPoolDashboard />
          </div>
        );
      case 'checks':
        return (
          <div className="page-content">
            <div className="page-header">
              <h1>🪙 CHECKING THIS</h1>
              <p>Parts in test</p>
            </div>
            {/* <SolanaTokensDashboard /> */}
            <WatchlistTrading />
          </div>
        );
      default:
        return (
          <div className="page-content" style={{ display: 'flex', height: '100%', flexDirection: 'column', gap: '24px', padding: '24px', borderRadius: '12px' }}>
            <div className="page-header">
              <div>
                <h1>🚀 Solana Trading Bot Dashboard</h1>
                <p>Welcome back, {user?.username}!</p>
              </div>
              <button
                onClick={logout}
                className="futuristic-glow animated-button"
                style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                Logout
              </button>
            </div>
            <div className="dashboard-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              <div className="content-card animated-card">
                <h3 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>User Profile</h3>
                <p><strong>Username:</strong> {user?.username}</p>
                <p><strong>Email:</strong> {user?.email}</p>
                <p><strong>User ID:</strong> {user?.id}</p>
              </div>
              <div className="content-card animated-card">
                <h3 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Quick Actions</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={() => setCurrentView('wallets')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>💼 Wallets</button>
                  <button onClick={() => setCurrentView('assets')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>💰 Assets</button>
                  <button onClick={() => setCurrentView('discover')} className="futuristic-glow animated-button" style={{ display: 'none', padding: '8px 12px', fontSize: '0.85rem' }}>🔍 Discover</button>
                  <button onClick={() => setCurrentView('tokens')} className="futuristic-glow animated-button" style={{ display: 'none', padding: '8px 12px', fontSize: '0.85rem' }}>📊 Watchlist</button>
                  <button onClick={() => setCurrentView('trading')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>🚀 Trading</button>
                  <button onClick={() => setCurrentView('compact-trading')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>📱 Enhanced</button>
                  <button onClick={() => setCurrentView('solana-tokens')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>🎨 Top Tokens</button>
                  {/* <button onClick={() => setCurrentView('monitor')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>📊 Monitor</button> */}
                  <button onClick={() => setCurrentView('history')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>📈 History</button>
                  <button onClick={() => setCurrentView('alerts')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>🔔 Alerts</button>
                  <button onClick={() => setCurrentView('opportunities')} className="futuristic-glow animated-button" style={{ display: 'none', padding: '8px 12px', fontSize: '0.85rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: '2px solid #667eea' }}>📈 Opportunities</button>
                  <button onClick={() => setCurrentView('investment')} className="futuristic-glow animated-button" style={{ display: 'none', padding: '8px 12px', fontSize: '0.85rem', background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', border: '2px solid #28a745' }}>🏦 Investment</button>
                  <button onClick={() => setCurrentView('theme-demo')} className="futuristic-glow animated-button" style={{ display: 'none', padding: '8px 12px', fontSize: '0.85rem' }}>🎨 Theme</button>
                  <button onClick={() => setCurrentView('jupiter-trading')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>🚀 Jupiter Trading</button>
                  <button onClick={() => setCurrentView('checks')} className="futuristic-glow animated-button" style={{ padding: '8px 12px', fontSize: '0.85rem' }}>🎨 Checks</button>
                </div>
              </div>
              <div className="content-card animated-card" style={{ display: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📈 Trading Opportunities
                    <span style={{ background: '#ff6b35', color: 'white', fontSize: '8px', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>NEW</span>
                  </h3>
                </div>
                <OpportunitiesSummary
                  onViewAll={() => setCurrentView('opportunities')}
                  compact={true}
                />
              </div>
              <div className="content-card animated-card" style={{ display: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🏦 Investment Pools
                    <span style={{ background: '#28a745', color: 'white', fontSize: '8px', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>NEW</span>
                  </h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Available Pools:</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-primary)' }}>5 Active</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Best 30d Return:</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#28a745' }}>+24.5%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Min Investment:</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-primary)' }}>0.1 SOL</span>
                  </div>
                  <button
                    onClick={() => setCurrentView('investment')}
                    className="futuristic-glow animated-button"
                    style={{
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      marginTop: '8px',
                      background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                      border: '2px solid #28a745',
                      width: '100%'
                    }}
                  >
                    View All Pools
                  </button>
                </div>
              </div>
              <div className="content-card animated-card">
                <h3 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Recent Trading Activity</h3>
                <TradingHistorySummary onViewAll={() => setCurrentView('history')} />
              </div>
            </div>
          </div>
        );
    }
  };

  React.useEffect(() => {
    navigate(`/app/${currentView}`, { replace: true });
  }, [currentView, navigate]);

  return (
    <AppLayout currentView={currentView} setCurrentView={setCurrentView}>
      {renderView()}
    </AppLayout>
  );
};

function App() {
  return (
    <ErrorBoundary name="App" title="Application Error" message="The application encountered an unexpected error.">
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <WalletAdapterProvider>
              <WalletProvider>
                <div className="App futuristic-app-bg">
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route
                      path="/app"
                      element={<Navigate to="/app/solana-tokens" replace />}
                    />
                    <Route
                      path="/app/:view"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  <ToastContainer />
                  <AlertsContainer />
                  {process.env.NODE_ENV === 'development' && <WalletPersistenceDebug />}
                </div>
              </WalletProvider>
            </WalletAdapterProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;